import React, { useEffect, useReducer, useState } from 'react';
import { Resource } from '../../constants/resources';
import { X, ArrowLeft, Phone, Copy, Check } from 'lucide-react';
import OptimizedImage from '../common/OptimizedImage';
import { useModal } from '../../contexts/ModalContext';
import { useResourceAction } from '../../hooks/useResourceAction';

interface ResourceDetailsModalProps {
  resource: Resource | null;
  isOpen: boolean;
  onClose: () => void;
  initialShowPayment?: boolean;
}

interface ModalState {
  showPaymentInfo: boolean;
  copiedText: string | null;
}

type ModalAction =
  | { type: 'SET_SHOW_PAYMENT'; payload: boolean }
  | { type: 'SET_COPIED_TEXT'; payload: string | null }
  | { type: 'RESET_MODAL'; initialShowPayment: boolean };

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'SET_SHOW_PAYMENT':
      return { ...state, showPaymentInfo: action.payload };
    case 'SET_COPIED_TEXT':
      return { ...state, copiedText: action.payload };
    case 'RESET_MODAL':
      return { showPaymentInfo: action.initialShowPayment, copiedText: null };
    default:
      return state;
  }
};

const ResourceDetailsModal: React.FC<ResourceDetailsModalProps> = ({ resource, isOpen, onClose, initialShowPayment = false }) => {
  const { setIsModalOpen } = useModal();
  const { handleResourceAction } = useResourceAction();
  const [state, dispatch] = useReducer(modalReducer, {
    showPaymentInfo: false,
    copiedText: null
  });

  const handleCopyToClipboard = async (text: string) => {
    try {
      // Prefer modern async clipboard API when available in a secure context
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        dispatch({ type: 'SET_COPIED_TEXT', payload: text });
        setTimeout(() => dispatch({ type: 'SET_COPIED_TEXT', payload: null }), 2000);
        return;
      }

      // Fallback for older browsers / insecure contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '1px';
      textArea.style.height = '1px';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        dispatch({ type: 'SET_COPIED_TEXT', payload: text });
        setTimeout(() => dispatch({ type: 'SET_COPIED_TEXT', payload: null }), 2000);
      }
    } catch (error) {
      // Avoid unhandled promise rejections while allowing debugging
      // eslint-disable-next-line no-console
      console.error('Failed to copy to clipboard', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setIsModalOpen(true);
      dispatch({ type: 'RESET_MODAL', initialShowPayment });
    } else {
      document.body.style.overflow = 'unset';
      setIsModalOpen(false);
    }
    return () => {
      document.body.style.overflow = 'unset';
      setIsModalOpen(false);
    };
  }, [isOpen, setIsModalOpen, initialShowPayment]);

  if (!isOpen || !resource) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with Glassmorphism */}
      <div 
        className="absolute inset-0 bg-stone-900/30 backdrop-blur-md transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[90vh] animate-scale-in">
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white/50 backdrop-blur-sm hover:bg-white rounded-full text-stone-500 hover:text-stone-800 transition-all border border-stone-200"
        >
          <X size={24} />
        </button>

        {/* Show resource details */}
        {!state.showPaymentInfo ? (
          <>
            {/* Image Section */}
            <div className="w-full md:w-1/2 h-80 md:h-auto bg-contessa-50 relative shrink-0 flex items-center justify-center p-6">
              {resource.imageUrl ? (
                <OptimizedImage
                  src={resource.imageUrl} 
                  alt={resource.title} 
                  className="w-full h-full object-contain"
                  priority={true} // Prioritize modal image as it is user-initiated
                />
              ) : (
                 <div className="w-full h-full flex items-center justify-center text-contessa-300">
                    {/* Fallback pattern or icon */}
                    <div className="w-20 h-20 bg-contessa-100 rounded-full opacity-50" />
                 </div>
              )}
              <div className="absolute bottom-4 left-4 flex gap-2 flex-wrap">
                 {resource.type.map((t) => (
                    <span key={t} className="px-3 py-1 bg-white/90 backdrop-blur text-contessa-800 text-xs font-bold rounded-lg shadow-sm">
                        {t}
                    </span>
                 ))}
              </div>
            </div>

            {/* Content Section */}
            <div className="flex-1 flex flex-col p-6 md:p-10 overflow-y-auto">
              <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mb-4 leading-tight">
                {resource.title}
              </h2>

              <div className="flex items-center gap-4 mb-8">
                <div className="flex items-baseline gap-2 text-contessa-800">
                   {resource.previousPriceDolars && (
                      <span className="text-lg text-stone-400 line-through decoration-stone-400 px-1">
                        {resource.previousPriceDolars}
                      </span>
                   )}
                   <span className="text-3xl font-bold">{resource.priceDolars || 'Gratis'}</span>
                </div>
              </div>

              <div className="prose prose-stone max-w-none text-stone-600 text-lg leading-relaxed mb-10 overflow-y-auto custom-scrollbar pr-2">
                <p className="whitespace-pre-line">{resource.description}</p>
              </div>

              <div className="mt-auto pt-6 border-t border-stone-100 flex justify-end">
                <button
                  onClick={() => {
                    handleResourceAction(
                      resource,
                      () => dispatch({ type: 'SET_SHOW_PAYMENT', payload: true }),
                      () => onClose() // Cerrar modal después de iniciar descarga
                    );
                  }}
                  className="w-full md:w-auto px-8 py-4 bg-contessa-800 hover:bg-contessa-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 text-center flex items-center justify-center gap-2"
                >
                  {resource.buttonText || 'Obtener recurso'}
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Payment Info Section */
          <div className="flex-1 flex flex-col p-6 md:p-10 w-full overflow-y-auto custom-scrollbar">
            <h2 className="font-serif text-3xl md:text-4xl text-stone-900 mb-2 leading-tight">
              ¿Cómo obtener el recurso?
            </h2>
            
            <p className="text-stone-600 text-lg mb-10 leading-relaxed">
              Te presento las formas de pago disponibles para acceder a <span className="font-semibold text-contessa-800">{resource.title}</span>
            </p>

            {/* Payment Method - Bank Transfer */}
            <div className="bg-contessa-50 rounded-xl p-8 mb-8 border-2 border-contessa-200">
              <h3 className="font-serif text-2xl text-stone-900 mb-4">Pago por depósito o transferencia bancaria</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-stone-600 text-sm mb-1">Número de cuenta Nuevo:</p>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-contessa-700">001283425 - 00002</span>
                    <button
                      onClick={() => handleCopyToClipboard('00128342500002')}
                      className="p-1 text-stone-400 hover:text-contessa-700 transition-colors hover:bg-white rounded"
                      title="Copiar al portapapeles"
                    >
                      {state.copiedText === '00128342500002' ? (
                        <Check size={16} className="text-green-600" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-stone-600 text-sm mb-1">Número de cuenta Antiguo:</p>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-contessa-700">179 - 2105206</span>
                    <button
                      onClick={() => handleCopyToClipboard('1792105206')}
                      className="p-1 text-stone-400 hover:text-contessa-700 transition-colors hover:bg-white rounded"
                      title="Copiar al portapapeles"
                    >
                      {state.copiedText === '1792105206' ? (
                        <Check size={16} className="text-green-600" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-stone-500 text-sm mt-4">
                Realiza la transferencia con tu banco habitual y luego envía el comprobante para confirmar tu compra.
              </p>
            </div>

            {/* Contact Methods */}
            <div className="mb-8">
              <h3 className="font-serif text-2xl text-stone-900 mb-6">Envía el comprobante de compra a través de estos medios</h3>
              <div className="space-y-4">
                {/* WhatsApp */}
                <a 
                  href={`https://wa.me/59898816513?text=${encodeURIComponent(`Hola Marha, tengo interés en ${resource.title}`)}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-5 bg-emerald-50 hover:bg-emerald-100 rounded-xl border-2 border-emerald-200 transition-all hover:shadow-md"
                >
                  <Phone className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-bold text-stone-900">WhatsApp</h4>
                    <p className="text-stone-600 text-sm">Comunícate directamente conmigo</p>
                  </div>
                  <span className="text-emerald-600 font-semibold">→</span>
                </a>

                {/* Email */}
                <button
                  onClick={() => handleCopyToClipboard('losciclosdemarha@gmail.com')}
                  className="w-full flex items-center gap-4 p-5 bg-blue-50 hover:bg-blue-100 rounded-xl border-2 border-blue-200 transition-all hover:shadow-md cursor-pointer"
                >
                  {state.copiedText === 'losciclosdemarha@gmail.com' ? (
                    <Check className="w-6 h-6 text-green-600 flex-shrink-0" />
                  ) : (
                    <Copy className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  )}
                  <div className="flex-1 text-left">
                    <h4 className="font-bold text-stone-900">
                      {state.copiedText === 'losciclosdemarha@gmail.com' ? '¡Email copiado!' : 'Copiar email'}
                    </h4>
                    <p className="text-stone-600 text-sm">losciclosdemarha@gmail.com</p>
                  </div>
                  <span className="text-blue-600 font-semibold">
                    {state.copiedText === 'losciclosdemarha@gmail.com' ? '✓' : '→'}
                  </span>
                </button>
              </div>
            </div>

            {/* Back Button */}
            <div className="mt-auto pt-6 border-t border-stone-100 flex items-center justify-between">
              <button
                onClick={() => dispatch({ type: 'SET_SHOW_PAYMENT', payload: false })}
                className="flex items-center gap-2 px-6 py-3 text-contessa-700 font-semibold hover:text-contessa-900 transition-colors"
              >
                <ArrowLeft size={20} />
                Volver a detalles
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceDetailsModal;
